import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import AppLayout from '../components/AppLayout';
import { getDemoAuth } from '@/hooks/useAppAuth';
import { REGION_HIERARCHY, ALL_PROVINCES } from '@/mocks/regionData';
import AgreementSection from '@/components/feature/AgreementSection';

const ALL_DAYS = ['월', '화', '수', '목', '금', '토', '일'];
const TIME_SLOTS = ['오전(06~12)', '오후(12~18)', '저녁(18~22)', '야간(22~06)', '종일'];

function toggleArrayItem(arr: string[], setter: (v: string[]) => void, item: string) {
  if (arr.includes(item)) {
    setter(arr.filter((x) => x !== item));
  } else {
    setter([...arr, item]);
  }
}

export default function RegisterCaregiverPage() {
  const navigate = useNavigate();
  const [userId, setUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isUpdate, setIsUpdate] = useState(false);

  const [experienceYears, setExperienceYears] = useState('');
  const [certificatesText, setCertificatesText] = useState('');
  const [introduction, setIntroduction] = useState('');
  const [hourlyRate, setHourlyRate] = useState('');
  const [earliestStartDate, setEarliestStartDate] = useState('');
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [selectedTimeSlots, setSelectedTimeSlots] = useState<string[]>([]);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [thirdPartyChecked, setThirdPartyChecked] = useState(false);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [existingPhotoUrl, setExistingPhotoUrl] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // 어떤 시/도가 펼쳐져 있는지
  const [expandedProvinces, setExpandedProvinces] = useState<string[]>([]);

  useEffect(() => {
    async function init() {
      const demo = getDemoAuth();
      const uid = demo?.userId || null;
      if (!uid) { setLoading(false); return; }
      setUserId(uid);

      const { data: existing } = await supabase
        .from('caregiver_profiles')
        .select('*')
        .eq('user_id', uid)
        .maybeSingle();

      if (existing) {
        setIsUpdate(true);
        setExperienceYears(existing.experience_years ? String(existing.experience_years) : '');
        setCertificatesText(existing.certificates?.join(', ') || '');
        setIntroduction(existing.introduction || '');
        setHourlyRate(existing.hourly_rate ? String(existing.hourly_rate) : '');
        setEarliestStartDate(existing.earliest_start_date || '');
        setSelectedRegions(existing.available_regions || []);
        setSelectedDays(existing.available_days || []);
        setSelectedTimeSlots(existing.available_times || []);
        if (existing.photo_url) {
          setExistingPhotoUrl(existing.photo_url);
        }
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('roles, caregiver_number')
        .eq('id', uid)
        .maybeSingle();

      if (profile) {
        if (!profile.roles?.includes('caregiver')) {
          await supabase.from('profiles').update({
            roles: [...(profile.roles || []), 'caregiver'],
          }).eq('id', uid);

          const stored = localStorage.getItem('kc_demo_login');
          if (stored) {
            const parsed = JSON.parse(stored);
            const newRoles = [...(parsed.roles || []), 'caregiver'];
            parsed.roles = newRoles;
            localStorage.setItem('kc_demo_login', JSON.stringify(parsed));
          }
        }

        if (!profile.caregiver_number) {
          const today = new Date();
          const dateStr = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
          const { data: maxNum } = await supabase
            .from('profiles')
            .select('caregiver_number')
            .ilike('caregiver_number', `CARE-${dateStr}-%`)
            .order('caregiver_number', { ascending: false })
            .limit(1)
            .maybeSingle();
          let seq = 1;
          if (maxNum?.caregiver_number) {
            const parts = maxNum.caregiver_number.split('-');
            const lastSeq = parseInt(parts[2], 10);
            if (!isNaN(lastSeq)) seq = lastSeq + 1;
          }
          await supabase.from('profiles').update({
            caregiver_number: `CARE-${dateStr}-${String(seq).padStart(3, '0')}`,
          }).eq('id', uid);
        }
      }

      setLoading(false);
    }
    init();
  }, []);

  const isFormValid =
    experienceYears && parseInt(experienceYears, 10) >= 0 &&
    hourlyRate && parseInt(hourlyRate, 10) > 0 &&
    earliestStartDate &&
    selectedRegions.length > 0 &&
    selectedDays.length > 0 &&
    selectedTimeSlots.length > 0 &&
    privacyChecked &&
    thirdPartyChecked;

  function toggleProvinceExpand(province: string) {
    setExpandedProvinces((prev) =>
      prev.includes(province) ? prev.filter((p) => p !== province) : [...prev, province]
    );
  }

  function toggleDistrict(province: string, district: string) {
    const regionKey = `${province} ${district}`;
    toggleArrayItem(selectedRegions, setSelectedRegions, regionKey);
  }

  function getProvinceSelectedCount(province: string): number {
    const districts = REGION_HIERARCHY[province] || [];
    return districts.filter((d) => selectedRegions.includes(`${province} ${d}`)).length;
  }

  function getProvinceTotalCount(province: string): number {
    return (REGION_HIERARCHY[province] || []).length;
  }

  function handlePhotoSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('이미지 파일만 업로드할 수 있습니다.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('파일 크기는 5MB 이하여야 합니다.');
      return;
    }

    setPhotoFile(file);
    setExistingPhotoUrl(null);

    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  }

  function handlePhotoRemove() {
    setPhotoFile(null);
    setPhotoPreview(null);
    setExistingPhotoUrl(null);
  }

  async function uploadPhoto(userId: string): Promise<string | null> {
    if (!photoFile) return existingPhotoUrl;

    setUploadingPhoto(true);
    try {
      const fileExt = photoFile.name.split('.').pop() || 'jpg';
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `${userId}/${fileName}`;

      const { error: uploadErr } = await supabase.storage
        .from('caregiver-photos')
        .upload(filePath, photoFile, {
          cacheControl: '3600',
          upsert: true,
        });

      if (uploadErr) {
        // Bucket might not exist, show helpful error
        if (uploadErr.message?.includes('bucket') || uploadErr.message?.includes('not found')) {
          throw new Error('사진 저장소가 설정되지 않았습니다. 관리자에게 문의해주세요.');
        }
        throw uploadErr;
      }

      const { data: urlData } = supabase.storage
        .from('caregiver-photos')
        .getPublicUrl(filePath);

      return urlData.publicUrl;
    } finally {
      setUploadingPhoto(false);
    }
  }

  async function handleSubmit() {
    if (!userId) return;
    setError('');
    setSuccess('');
    setSaving(true);

    const certs = certificatesText
      .split(/[,，、]/)
      .map((s) => s.trim())
      .filter(Boolean);

    try {
      let photoUrl = existingPhotoUrl;

      // Upload new photo if selected
      if (photoFile) {
        photoUrl = await uploadPhoto(userId);
      }

      const payload = {
        user_id: userId,
        experience_years: parseInt(experienceYears, 10),
        hourly_rate: parseInt(hourlyRate, 10),
        available_regions: selectedRegions,
        available_days: selectedDays,
        available_times: selectedTimeSlots,
        certificates: certs.length > 0 ? certs : null,
        introduction: introduction || null,
        earliest_start_date: earliestStartDate,
        is_active: true,
        photo_url: photoUrl,
      };

      if (isUpdate) {
        const { error: updateErr } = await supabase
          .from('caregiver_profiles')
          .update(payload)
          .eq('user_id', userId);
        if (updateErr) throw updateErr;
      } else {
        const { error: insertErr } = await supabase
          .from('caregiver_profiles')
          .insert(payload);
        if (insertErr) throw insertErr;
      }

      setSuccess(isUpdate ? '간병인 정보가 수정되었습니다.' : '간병인 등록이 완료되었습니다.');
      setTimeout(() => navigate('/app', { replace: true }), 1200);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : '저장 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-[#1976D2] border-t-transparent rounded-full animate-spin" />
        </div>
      </AppLayout>
    );
  }

  if (!userId) {
    return (
      <AppLayout>
        <div className="min-h-screen flex flex-col items-center justify-center px-6">
          <p className="text-gray-500 mb-4">로그인이 필요합니다.</p>
          <button onClick={() => navigate('/app/login')} className="text-[#1976D2] font-bold cursor-pointer">로그인하기</button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="min-h-screen px-5 pt-6 pb-24 bg-white">
        {/* 헤더 */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate('/app')} className="w-9 h-9 rounded-xl border border-gray-200 flex items-center justify-center cursor-pointer hover:bg-gray-50">
            <i className="ri-arrow-left-line text-gray-600" />
          </button>
          <div>
            <h1 className="text-lg font-extrabold text-gray-900">{isUpdate ? '간병인 정보 수정' : '간병인 등록'}</h1>
            <p className="text-xs text-gray-400">경력과 근무 조건을 입력해주세요</p>
          </div>
        </div>

        {/* 경력 및 자격증 */}
        <div className="space-y-4 mb-6">
          <div className="p-4 rounded-xl bg-blue-50 border border-blue-200">
            <div className="flex items-center gap-2 mb-1">
              <i className="ri-nurse-line text-[#1976D2]" />
              <span className="text-sm font-bold text-[#1976D2]">경력 및 자격증</span>
            </div>
            <p className="text-xs text-gray-500">정확한 정보일수록 매칭 확률이 높아집니다.</p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              간병 경력 <span className="text-red-400">*</span>
            </label>
            <div className="flex items-center gap-2">
              <input type="number" value={experienceYears} onChange={(e) => setExperienceYears(e.target.value)} placeholder="예: 5" min={0} max={50} className="flex-1 px-4 py-3 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#1976D2] bg-white" />
              <span className="text-sm text-gray-500 whitespace-nowrap">년</span>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              보유 자격증 <span className="text-gray-400 font-normal">(선택)</span>
            </label>
            <input type="text" value={certificatesText} onChange={(e) => setCertificatesText(e.target.value)} placeholder="예: 요양보호사 1급, 간호조무사 (쉼표로 구분)" className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#1976D2] bg-white" />
            <p className="text-xs text-gray-400 mt-1">쉼표(,)로 구분하여 여러 자격증을 입력할 수 있습니다.</p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              자기소개 <span className="text-gray-400 font-normal">(선택)</span>
            </label>
            <textarea value={introduction} onChange={(e) => setIntroduction(e.target.value.slice(0, 500))} placeholder="환자/보호자에게 보여질 자기소개를 입력해주세요." rows={3} maxLength={500} className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#1976D2] bg-white resize-none" />
            <p className="text-xs text-gray-400 mt-1 text-right">{introduction.length}/500</p>
          </div>
        </div>

        {/* 프로필 사진 (선택) */}
        <div className="space-y-3 mb-6">
          <div className="flex items-center gap-2">
            <i className="ri-camera-line text-[#1976D2]" />
            <span className="text-sm font-bold text-[#1976D2]">프로필 사진</span>
            <span className="text-[10px] text-gray-400 font-normal">(선택)</span>
          </div>
          <p className="text-xs text-gray-400">사진이 있으면 매칭 확률이 높아집니다. 넣지 않으셔도 괜찮아요.</p>

          {(photoPreview || existingPhotoUrl) ? (
            <div className="relative inline-block">
              <div className="w-28 h-28 rounded-2xl overflow-hidden border-2 border-gray-200">
                <img
                  src={photoPreview || existingPhotoUrl || ''}
                  alt="프로필 사진 미리보기"
                  className="w-full h-full object-cover"
                />
              </div>
              <button
                onClick={handlePhotoRemove}
                className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center cursor-pointer hover:bg-red-600 transition-colors shadow-sm"
              >
                <i className="ri-close-line text-xs" />
              </button>
            </div>
          ) : (
            <label className="flex flex-col items-center gap-2 p-6 rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50 cursor-pointer hover:border-[#1976D2] hover:bg-blue-50/30 transition-all">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <i className="ri-camera-line text-xl text-[#1976D2]" />
              </div>
              <span className="text-xs text-gray-500 font-medium">사진 선택하기</span>
              <span className="text-[10px] text-gray-400">JPG, PNG · 최대 5MB</span>
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoSelect}
                className="hidden"
              />
            </label>
          )}
        </div>

        {/* 근무 조건 */}
        <div className="space-y-4 mb-6">
          <div className="flex items-center gap-2 mb-1">
            <i className="ri-time-line text-[#1976D2]" />
            <span className="text-sm font-bold text-[#1976D2]">근무 조건</span>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                희망 시급 <span className="text-red-400">*</span>
              </label>
              <div className="flex items-center gap-2">
                <input type="number" value={hourlyRate} onChange={(e) => setHourlyRate(e.target.value)} placeholder="예: 15000" min={0} step={1000} className="flex-1 px-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#1976D2] bg-white" />
                <span className="text-xs text-gray-400 whitespace-nowrap">원/시간</span>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                빠른 시작일 <span className="text-red-400">*</span>
              </label>
              <input type="date" value={earliestStartDate} onChange={(e) => setEarliestStartDate(e.target.value)} min={new Date().toISOString().split('T')[0]} className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#1976D2] bg-white cursor-pointer" />
            </div>
          </div>

          {/* 계층형 지역 선택 */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-2">
              간병 가능 지역 <span className="text-red-400">*</span>
            </label>
            {selectedRegions.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {selectedRegions.map((region) => (
                  <span key={region} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-xs text-[#1976D2] font-medium">
                    {region}
                    <button
                      onClick={() => setSelectedRegions((prev) => prev.filter((r) => r !== region))}
                      className="w-4 h-4 rounded-full flex items-center justify-center hover:bg-blue-100 cursor-pointer"
                    >
                      <i className="ri-close-line text-[10px]" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <p className="text-xs text-gray-400 mb-3">
              간병 가능한 시/도를 터치하여 구/시/군을 선택하세요. 여러 지역을 중복 선택할 수 있습니다.
            </p>
            <div className="space-y-2">
              {ALL_PROVINCES.map((province) => {
                const isExpanded = expandedProvinces.includes(province);
                const selectedCount = getProvinceSelectedCount(province);
                const totalCount = getProvinceTotalCount(province);
                const districts = REGION_HIERARCHY[province] || [];

                return (
                  <div key={province} className="rounded-xl border border-gray-100 overflow-hidden">
                    {/* 시/도 헤더 */}
                    <button
                      onClick={() => toggleProvinceExpand(province)}
                      className={`w-full flex items-center justify-between px-4 py-3 cursor-pointer transition-colors ${
                        isExpanded ? 'bg-blue-50' : 'bg-white hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-semibold ${selectedCount > 0 ? 'text-[#1976D2]' : 'text-gray-700'}`}>
                          {province}
                        </span>
                        {selectedCount > 0 && (
                          <span className="px-1.5 py-0.5 rounded-md bg-[#1976D2] text-[10px] font-bold text-white">
                            {selectedCount}/{totalCount}
                          </span>
                        )}
                      </div>
                      <i className={`ri-arrow-down-s-line text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>

                    {/* 구/시/군 목록 */}
                    {isExpanded && (
                      <div className="px-4 py-3 bg-gray-50/50 border-t border-gray-100">
                        <div className="flex flex-wrap gap-1.5">
                          {districts.map((district) => {
                            const regionKey = `${province} ${district}`;
                            const isSelected = selectedRegions.includes(regionKey);
                            return (
                              <button
                                key={regionKey}
                                onClick={() => toggleDistrict(province, district)}
                                className={`px-2.5 py-1.5 rounded-lg border text-xs cursor-pointer transition-all whitespace-nowrap ${
                                  isSelected
                                    ? 'border-[#1976D2] text-[#1976D2] bg-blue-50 font-medium'
                                    : 'border-gray-200 text-gray-500 bg-white hover:border-gray-300'
                                }`}
                              >
                                {district}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-2">
              간병 가능 요일 <span className="text-red-400">*</span>
            </label>
            <div className="flex gap-2">
              {ALL_DAYS.map((day) => (
                <button
                  key={day}
                  onClick={() => toggleArrayItem(selectedDays, setSelectedDays, day)}
                  className={`w-10 h-10 rounded-full border-2 text-sm font-medium cursor-pointer transition-all ${
                    selectedDays.includes(day)
                      ? 'border-[#1976D2] text-[#1976D2] bg-blue-50'
                      : 'border-gray-100 text-gray-500 bg-white hover:border-gray-200'
                  }`}
                >
                  {day}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-2">
              간병 가능 시간대 <span className="text-red-400">*</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {TIME_SLOTS.map((slot) => (
                <button
                  key={slot}
                  onClick={() => toggleArrayItem(selectedTimeSlots, setSelectedTimeSlots, slot)}
                  className={`px-3 py-2 rounded-lg border text-xs font-medium cursor-pointer transition-all whitespace-nowrap ${
                    selectedTimeSlots.includes(slot)
                      ? 'border-[#1976D2] text-[#1976D2] bg-blue-50'
                      : 'border-gray-100 text-gray-500 bg-white hover:border-gray-200'
                  }`}
                >
                  {slot}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 간병비 정산 안내 */}
        <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 mb-6">
          <div className="flex items-start gap-2.5">
            <i className="ri-information-line text-amber-600 mt-0.5" />
            <div className="text-xs text-amber-800 leading-relaxed">
              <p className="font-semibold mb-1">간병비 정산 안내</p>
              <p>간병비는 환자/보호자와 직접 정산합니다. 간병 완료 후 회사에 중개 수수료를 납부합니다.</p>
            </div>
          </div>
        </div>

        {error && <div className="mb-4 p-3 rounded-xl bg-red-50 text-xs text-red-500 text-center">{error}</div>}
        {success && <div className="mb-4 p-3 rounded-xl bg-green-50 text-xs text-green-600 text-center font-bold">{success}</div>}

        {/* 약관 동의 */}
        <div className="mb-6">
          <AgreementSection
            type="caregiver"
            privacyChecked={privacyChecked}
            thirdPartyChecked={thirdPartyChecked}
            onPrivacyChange={setPrivacyChecked}
            onThirdPartyChange={setThirdPartyChecked}
            disabled={saving}
          />
        </div>

        <button
          onClick={handleSubmit}
          disabled={!isFormValid || saving || uploadingPhoto}
          className="w-full py-3.5 rounded-xl text-sm font-bold text-white whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50"
          style={{ backgroundColor: '#1976D2' }}
        >
          {saving || uploadingPhoto ? (
            <span className="flex items-center justify-center gap-2">
              <i className="ri-loader-4-line animate-spin" />저장 중...
            </span>
          ) : (
            isUpdate ? '간병인 정보 수정하기' : '간병인 등록하기'
          )}
        </button>
      </div>
    </AppLayout>
  );
}